import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
// Through the importmap (snippets/scripts.liquid) so each module URL carries
// asset_url's version: a relative './x.js' import is cached by the CDN for a
// year and kept serving the previous file after a deploy.
import { GiftCardStyleChooser } from '@theme/custom-pdp-gift-card-styles';
import { GiftCardCustomAmount } from '@theme/custom-pdp-gift-card-amount';
import { GiftCardVariantMap } from '@theme/custom-pdp-gift-card-variant';

/**
 * "Gift Card Form" block — drives Shopify's native gift card recipient fields
 * (field map in snippets/custom-pdp-gift-card-form.liquid) so the platform,
 * not an app, emails the card. Amount pill × confirmed card style resolves to
 * one product variant, so the card is issued at that denomination with that
 * artwork.
 *
 * Elements inside `<product-form-component>` belong to that component's refs,
 * so everything here is looked up with plain attribute selectors.
 */
class GiftCardFormBlock extends Component {
  /** @type {AbortController | null} */
  #abort = null;

  /** @type {GiftCardStyleChooser | null} */
  #styles = null;

  /** @type {GiftCardVariantMap | null} */
  #variants = null;

  /** Last denomination pill ticked; the "custom" pill leaves it in place. */
  #lastAmount = '';

  #amount = new GiftCardCustomAmount(this);

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    document.addEventListener(ThemeEvents.cartError, this.#onCartError, { signal });
    // Runs before product-form's submit handler: an out-of-range custom amount stops the add.
    this.addEventListener('submit', this.#guardSubmit, { capture: true, signal });
    // The CTA stays disabled until every required field is filled.
    this.addEventListener('input', this.#syncSubmit, { signal });
    this.addEventListener('input', this.#onInput, { signal });
    this.addEventListener('change', this.#syncSubmit, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#clearErrors, { signal });

    this.#variants = new GiftCardVariantMap(this);
    this.#styles = new GiftCardStyleChooser(this, (text) => this.#announce(text), signal, () => {
      this.#resolveVariant();
      this.#syncSubmit();
    });

    const amount = this.querySelector('input[name^="gift-card-amount-"]:checked');
    if (amount instanceof HTMLInputElement && amount.value !== 'custom') this.#lastAmount = amount.value;

    this.#syncRecipient();
    this.#syncDelivery();
    this.#syncCustomAmount();
    this.#syncSubmit();
  }

  /** @returns {HTMLButtonElement | null} */
  get #submit() {
    const el = document.querySelector(`button[type="submit"][form="${this.dataset.formId}"]`);
    return el instanceof HTMLButtonElement ? el : null;
  }

  /** @param {Event} event */
  #onInput = (event) => {
    const email = this.#input('recipientEmail');
    if (event.target === email && email?.checkValidity()) this.#showError('emailError', email, '');
  };

  /** Disables the CTA while a required field or the card style is missing, or the custom amount is out of range. */
  #syncSubmit = () => {
    const button = this.#submit;
    if (!button || button.dataset.soldOut === 'true') return;

    const form = this.querySelector('form');
    const required = form ? [...form.querySelectorAll('input[required]:not(:disabled)')] : [];
    // checkValidity covers required, the email pattern and the date bounds.
    const filled = required.every((el) => el instanceof HTMLInputElement && el.value.trim() !== '' && el.checkValidity());
    // The style chooser, when rendered, must have a confirmed design.
    const style = this.#input('styleChoice');
    const styled = !style || style.value !== '';
    button.disabled = !(filled && styled && this.#amount.valid);
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#abort = null;
  }


  /**
   * @param {string} ref
   * @returns {HTMLElement | null}
   */
  #el(ref) {
    return this.querySelector(`[ref="${ref}"]`);
  }

  /**
   * @param {string} ref
   * @returns {HTMLInputElement | null}
   */
  #input(ref) {
    const el = this.#el(ref);
    return el instanceof HTMLInputElement ? el : null;
  }


  // Amount

  /** Denomination pills re-resolve the variant; the "custom" pill only reveals its input. @param {Event} event */
  selectAmount(event) {
    const radio = event.target;
    if (!(radio instanceof HTMLInputElement)) return;

    if (radio.value !== 'custom') {
      this.#lastAmount = radio.value;
      this.#resolveVariant();
    }

    this.#syncCustomAmount();
  }

  /** Amount pill × confirmed style → hidden `id`. Nothing changes until both are known. */
  #resolveVariant() {
    if (!this.#lastAmount) return;
    const style = this.#input('styleChoice')?.value ?? '';
    const id = this.#variants?.resolve(this.#lastAmount, style);
    if (id) this.#variants?.apply(id);
  }

  #syncCustomAmount() {
    this.#amount.sync();
    this.#syncSubmit();
  }

  validateCustomAmount() {
    this.#amount.render(true);
  }

  clearCustomAmount() {
    this.#amount.clear();
  }

  /** @param {Event} event */
  #guardSubmit = (event) => {
    if (!this.#amount.active || this.#amount.render(false)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#amount.focus();
  };

  // Recipient

  toggleSelf() {
    this.#syncRecipient(true);
  }

  /**
   * Default: name, email and sender name required. "Send to myself": only the
   * email stays (own address); the Shopify flag stays on so scheduling works.
   * @param {boolean} [announce]
   */
  #syncRecipient(announce = false) {
    const toggle = this.#input('selfToggle');
    const group = toggle?.closest('.c-custom-pdp__gift-card-group--recipient');
    if (!toggle || !group) return;

    const toSelf = toggle.checked;
    const emailField = this.#el('recipientEmailField');
    const emailLabel = emailField?.querySelector('label');

    for (const ref of ['recipientName', 'senderName']) {
      const input = this.#input(ref);
      const field = this.#el(`${ref}Field`);
      if (field) field.hidden = toSelf;
      if (input) {
        input.disabled = toSelf;
        input.required = !toSelf;
        if (toSelf) input.value = '';
      }
    }

    group.dataset.self = String(toSelf);
    emailField?.classList.toggle('c-custom-pdp__gift-card-field--floating', toSelf);
    if (emailLabel) emailLabel.hidden = !toSelf;
    const email = this.#input('recipientEmail');
    if (email) {
      // Remember the original placeholder before the first swap.
      if (!email.dataset.placeholder) email.dataset.placeholder = email.placeholder;
      email.placeholder = toSelf ? ' ' : email.dataset.placeholder;
    }

    this.#clearErrors();
    this.#syncSubmit();
    if (announce) {
      this.#announce(toSelf ? 'Enter your own email to receive the gift card.' : 'Recipient fields shown.');
    }
  }

  /** Inline email check on blur; Shopify re-validates on /cart/add. */
  validateEmail() {
    const email = this.#input('recipientEmail');
    if (!email || email.disabled) return;
    const bad = email.value.trim() !== '' && !email.checkValidity();
    this.#showError('emailError', email, bad ? email.dataset.invalidMessage || '' : '');
  }

  // Delivery date

  selectDelivery() {
    this.#syncDelivery();
  }

  #syncDelivery() {
    const field = this.#el('sendOnField');
    const input = this.#input('sendOnInput');
    const offset = this.#input('timezoneOffset');
    if (!field || !input) return;

    const later = this.querySelector('input[value="later"]');
    const scheduling = later instanceof HTMLInputElement && later.checked;

    field.hidden = !scheduling;
    input.disabled = !scheduling;
    input.required = scheduling;

    if (offset) {
      offset.disabled = !scheduling;
      offset.value = scheduling ? String(new Date().getTimezoneOffset()) : '';
    }

    if (scheduling) {
      this.#setDateBounds(input);
      input.focus();
    } else {
      input.value = '';
      this.#showError('sendOnError', input, '');
    }
    this.#syncSubmit();
  }

  /**
   * Shopify accepts a send date from today up to 90 days ahead.
   * @param {HTMLInputElement} input
   */
  #setDateBounds(input) {
    const pad = (/** @type {number} */ n) => String(n).padStart(2, '0');
    const iso = (/** @type {Date} */ d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    const today = new Date();
    const max = new Date();
    max.setDate(today.getDate() + 90);

    input.min = iso(today);
    input.max = iso(max);
  }

  // Card style dialog (custom-pdp-gift-card-styles.js)

  openStyles() {
    this.#styles?.open();
  }

  closeStyles() {
    this.#styles?.close();
  }

  confirmStyle() {
    this.#styles?.confirm();
  }

  // Errors from /cart/add (Shopify validates the recipient fields)

  /** @param {Event} event */
  #onCartError = (event) => {
    const detail = /** @type {any} */ (event).detail;
    if (!detail || detail.sourceId !== this.dataset.formId) return;

    const { message, errors } = detail.data || {};
    this.#clearErrors();

    /** @type {Record<string, [string, string]>} */
    const map = {
      email: ['emailError', 'recipientEmail'],
      name: ['nameError', 'recipientName'],
      send_on: ['sendOnError', 'sendOnInput'],
    };

    let handled = false;
    if (errors && typeof errors === 'object') {
      for (const [field, messages] of Object.entries(errors)) {
        const target = map[field];
        if (!target) continue;
        // Shopify answers with the bare predicate ("is invalid"); lead with the field.
        const input = this.#input(target[1]);
        const label = input?.placeholder || input?.getAttribute('aria-label') || '';
        const text = `${label} ${Array.isArray(messages) ? messages.join(', ') : String(messages)}`.trim();
        this.#showError(target[0], input, text);
        handled = true;
      }
    }

    if (!handled && message) {
      this.#showError('formError', null, message);
    }

    this.#announce(message || 'There was a problem with the gift card details.');
  };

  /**
   * @param {string} ref
   * @param {HTMLInputElement | null} input
   * @param {string} text
   */
  #showError(ref, input, text) {
    const box = this.#el(ref);
    if (box) {
      box.textContent = text;
      box.hidden = !text;
    }
    if (input) {
      if (text) {
        input.setAttribute('aria-invalid', 'true');
        if (box?.id) input.setAttribute('aria-describedby', box.id);
      } else {
        input.removeAttribute('aria-invalid');
        input.removeAttribute('aria-describedby');
      }
    }
  }

  #clearErrors = () => {
    this.#showError('emailError', this.#input('recipientEmail'), '');
    this.#showError('nameError', this.#input('recipientName'), '');
    this.#showError('sendOnError', this.#input('sendOnInput'), '');
    this.#showError('formError', null, '');
  };

  /** @param {string} text */
  #announce(text) {
    const region = this.#el('statusRegion');
    if (region) region.textContent = text;
  }
}

if (!customElements.get('gift-card-form-block')) {
  customElements.define('gift-card-form-block', GiftCardFormBlock);
}
